import React from 'react';
import PropTypes from 'prop-types';
import { isEqual, isNull, isEmpty, keys, contains, map, omit, intersection } from 'underscore';
import { FilterViewManager } from '@bingads-webui/filter-view-manager';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { FilterEntity } from '@bingads-webui/filter-entity';
import { featureNameToUrlFilter, allowedUrlFilterKeys } from '@bingads-webui-campaign/url-filter-metadata';
import { featureNameToEntityNameFilter } from '@bingads-webui-campaign/entity-name-filter-metadata';
import { getPredicateIconClassName } from './get-predicate-icon-classname';

const featureNameToSelectableFilterKey = {
  CampaignsGrid: WaterfallFilterType.CampaignStatus,
  AdgroupGrid: WaterfallFilterType.AdGroupStatus,
};

const featureNameToPropertyName = {
  CampaignsGrid: 'CampaignStatus',
  AdgroupGrid: 'AdGroupStatus',
};

const featureNameToWaterfallFilterKey = {
  AdgroupGrid: [WaterfallFilterType.CampaignStatus, WaterfallFilterType.CampaignType],
  newProductGroupsGrid: [WaterfallFilterType.CampaignStatus, WaterfallFilterType.CampaignType],
};

const shouldCorrelateSelectableFilterWithWaterfall = featureName => contains(keys(featureNameToSelectableFilterKey), featureName);
const shouldCorrelateEditableFilterWithUrlFilter = featureName => contains(keys(featureNameToUrlFilter), featureName);
const shouldCorrelateEditableFilterWithEntityNameFilter = featureName => contains(keys(featureNameToEntityNameFilter), featureName);
class FilterViewManagerWithoutGlobalFilterContext extends React.Component {
  state = {
    selectableFilterValue: null,
    waterfallFilterValue: null,
    dateFilterPop: null,
    editableFilterValue: '',
    editableEntityNameFilterValue: '',
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {};
    // Selectable filter from main menu
    const newSelectableFilter = props[featureNameToSelectableFilterKey[props.featureName]];
    const newEditableFilter = props[featureNameToUrlFilter[props.featureName]];
    const newEditableEntityNameFilter = props[featureNameToEntityNameFilter[props.featureName]];

    if (shouldCorrelateSelectableFilterWithWaterfall(props.featureName) &&
      !isEqual(state.selectableFilterValue, newSelectableFilter)) {
      props.filterService.initializeCombinedFilterPromise.then(() => {
        // Update predicate on toolbar to reflect new selectable filter value
        // This is needed on initial load
        // The property name from global waterfall filter context is like 'AdGroupStatus'
        // But when applied in quick filter, it needs to be converted to 'Status'
        props.filterService.setQuickFilter('', {
          query: newSelectableFilter && newSelectableFilter.value && newSelectableFilter.value.query ? {
            Status: newSelectableFilter.value.query[featureNameToPropertyName[props.featureName]],
          } : null,
        });
      });
      newState.selectableFilterValue = newSelectableFilter;
    }

    if (shouldCorrelateEditableFilterWithUrlFilter(props.featureName) &&
      !isEqual(state.editableFilterValue, newEditableFilter)) {
      const filterObject = newEditableFilter ? JSON.parse(newEditableFilter) : '';
      if (filterObject) {
        props.filterService.initializeCombinedFilterPromise.then(() => {
          const urlFilterEntity = FilterEntity.fromLegacy(filterObject, {
            entitySchema: props.filterService.entitySchema,
            odata: props.filterService.odataOptions,
          });
          props.filterService.setEditableFilter(urlFilterEntity.query, undefined, undefined, () => {
            // remove url filter from url
            const { scope } = props;
            if (intersection(Object.keys(scope.values), allowedUrlFilterKeys).length > 0) {
              scope.values = omit(scope.values, allowedUrlFilterKeys);
              scope.history.replace({
                pathname: scope.history.location.pathname,
                search: scope.stringify(),
              });

              // reset url filter experession as we already set that to editable filter
              props.updateUrlFilter({
                filterType: featureNameToUrlFilter[props.featureName],
                filter: '',
              });
            }
          });
        });
      }
      newState.editableFilterValue = newEditableFilter;
    }

    if (shouldCorrelateEditableFilterWithEntityNameFilter(props.featureName) &&
      !isEqual(state.editableEntityNameFilterValue, newEditableEntityNameFilter)) {
      if (newEditableEntityNameFilter) {
        const filterObject = newEditableEntityNameFilter ? JSON.parse(newEditableEntityNameFilter) : '';
        props.filterService.initializeCombinedFilterPromise.then(() => {
          const entityNameFilterEntity = FilterEntity.fromLegacy(filterObject, {
            entitySchema: props.filterService.entitySchema,
            odata: props.filterService.odataOptions,
          });
          props.filterService.setEditableFilter(entityNameFilterEntity.query, undefined, undefined, () => {
            // reset entity name filter experession as we already set that to editable filter
            props.updateEntityNameFilter({
              filterType: featureNameToEntityNameFilter[props.featureName],
              filter: '',
            });
          });
        });
      }
      newState.editableEntityNameFilterValue = newEditableEntityNameFilter;
    }

    // Waterfall filter value from higher level entity is already updated during initializeCombinedFilter
    const waterfallFilerValues = map(featureNameToWaterfallFilterKey[props.featureName], waterfallFilterKey => props[waterfallFilterKey]);
    if (!isEqual(state.waterfallFilterValue, waterfallFilerValues)) {
      newState.waterfallFilterValue = waterfallFilerValues;
    }

    // Need to detect whether user has enabled pop
    if (props[WaterfallFilterType.DateFilter] && !isEqual(state.dateFilterPop, props[WaterfallFilterType.DateFilter].compared)) {
      newState.dateFilterPop = props[WaterfallFilterType.DateFilter].compared;
    }

    if (isEmpty(newState)) {
      return null;
    }
    return newState;
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(this.state.waterfallFilterValue, prevState.waterfallFilterValue)) {
      this.props.filterService.refreshWaterfallFilters();
    }
  }

  updateWaterfallFilter = (newFilterValue) => {
    this.setState({
      selectableFilterValue: newFilterValue,
    });
    this.props.updateWaterfallFilter({
      filterType: featureNameToSelectableFilterKey[this.props.featureName],
      filter: {
        key: featureNameToSelectableFilterKey[this.props.featureName],
        value: {
          query: isNull(newFilterValue) ? null : {
            [featureNameToPropertyName[this.props.featureName]]: {
              $in: newFilterValue,
            },
          },
        },
      },
    });
  }

  shouldCorrelateSelectableFilterWithWaterfall() {
    return this.props.featureName in keys(featureNameToSelectableFilterKey);
  }

  render() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { updateWaterfallFilter, permissions, ...passThrough } = this.props;
    return (<FilterViewManager
      updateWaterfallFilter={shouldCorrelateSelectableFilterWithWaterfall(this.props.featureName) ? this.updateWaterfallFilter : null}
      isPoPEnabled={!isNull(this.state.dateFilterPop)}
      getPredicateIconClassName={getPredicateIconClassName(permissions)}
      permissions={permissions}
      {...passThrough}
    />);
  }
}

FilterViewManagerWithoutGlobalFilterContext.propTypes = {
  selector: PropTypes.arrayOf(PropTypes.shape({
    property: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
    })),
    removable: PropTypes.bool,
    defaultValue: PropTypes.arrayOf(PropTypes.string),
    SelectableFilterKey: PropTypes.number,
  })),
  featureName: PropTypes.string,
  updateWaterfallFilter: PropTypes.func.isRequired,
  filterService: PropTypes.shape({
    initializeCombinedFilterPromise: PropTypes.shape({
      then: PropTypes.func,
    }),
    setEditableFilter: PropTypes.func,
    refreshWaterfallFilters: PropTypes.func,
  }).isRequired,
  readOnlyProperties: PropTypes.arrayOf(PropTypes.string),
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  updateUrlFilter: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}),
};

FilterViewManagerWithoutGlobalFilterContext.defaultProps = {
  selector: [],
  featureName: null,
  readOnlyProperties: [],
  permissions: undefined,
};


export const FilterViewManagerWithGlobalFilterContext = withScope(withGlobalFilter(FilterViewManagerWithoutGlobalFilterContext));
